import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, daysFromNow, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, PencilSimple, Trash, Warning } from "@phosphor-icons/react";
import PhotoUploader, { PhotoThumb } from "@/components/PhotoUploader";

const BLANK = {
  asset_tag: "", name: "", category: "General", location: "Plant",
  serial_number: "", install_date: "", service_interval_days: 90,
  last_service_date: "", next_service_date: "", status: "operational", notes: "",
  photo_url: null,
};

export default function Machines() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const canEdit = can(user.role, "staff");
  const canDelete = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/machines"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setEditOpen(true); }
  function openEdit(m) {
    setEditing(m);
    setForm({
      ...BLANK, ...m,
      install_date: m.install_date ? String(m.install_date).slice(0, 10) : "",
      last_service_date: m.last_service_date ? String(m.last_service_date).slice(0, 10) : "",
      next_service_date: m.next_service_date ? String(m.next_service_date).slice(0, 10) : "",
    });
    setEditOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    try {
      const toIso = (d) => d ? new Date(d).toISOString() : null;
      const payload = {
        ...form,
        service_interval_days: Number(form.service_interval_days),
        install_date: toIso(form.install_date),
        last_service_date: toIso(form.last_service_date),
        next_service_date: toIso(form.next_service_date),
      };
      if (editing) await api.put(`/machines/${editing.id}`, payload);
      else await api.post("/machines", payload);
      toast.success("Saved");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(m) {
    if (!window.confirm(`Delete machine ${m.asset_tag}?`)) return;
    try { await api.delete(`/machines/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="machines-page">
      <PageHeader
        overline="Module / Plant"
        title="Machine Service Plan"
        subtitle="Track assets, service intervals and never miss a scheduled maintenance."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `machines-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="machine-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Machine
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No machines." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th className="w-12"></th>
                  <th>Asset Tag</th><th>Name</th><th>Category</th><th>Location</th>
                  <th>Last Service</th><th>Next Service</th><th>Interval</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const days = daysFromNow(m.next_service_date);
                  const due = days !== null && days <= 14;
                  return (
                    <tr
                      key={m.id}
                      data-testid={`machine-row-${m.asset_tag}`}
                      onClick={() => nav(`/machines/${m.id}`)}
                      className="cursor-pointer hover:bg-[#f4f4f5]"
                    >
                      <td className="py-2">
                        <PhotoThumb value={m.photo_url} size={40} testId={`machine-thumb-${m.asset_tag}`} />
                      </td>
                      <td className="mono font-semibold">{m.asset_tag}</td>
                      <td>{m.name}</td>
                      <td className="text-sm text-[#52525b]">{m.category}</td>
                      <td className="text-sm">{m.location}</td>
                      <td className="text-sm text-[#52525b]">{fmtDate(m.last_service_date)}</td>
                      <td>
                        {m.next_service_date ? (
                          <div className={`text-sm ${due ? "text-[#FF2A2A] font-semibold" : ""}`}>
                            {due && <Warning size={12} weight="fill" className="inline mr-1" />}
                            {fmtDate(m.next_service_date)}{days !== null && ` (${days}d)`}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="text-sm mono">{m.service_interval_days}d</td>
                      <td><StatusBadge status={m.status} /></td>
                      <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {canEdit && (
                          <BtnGhost onClick={() => openEdit(m)}>
                            <PencilSimple size={12} weight="bold" />
                          </BtnGhost>
                        )}
                        {canDelete && (
                          <BtnGhost onClick={() => remove(m)} className="text-[#FF2A2A]">
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}
        title={editing ? `Edit ${editing.asset_tag}` : "Add Machine"} wide>
        <form onSubmit={save} className="space-y-4">
          <div className="border border-[#e4e4e7] p-4 bg-[#fafafa]">
            <div className="phizo-label mb-2">Equipment Photo</div>
            <PhotoUploader
              value={form.photo_url}
              onChange={(url) => setForm({ ...form, photo_url: url })}
              category="machine"
              size={96}
              testId="machine-photo"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
          <Field label="Asset Tag"><Input required data-testid="machine-tag" value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} /></Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="operational">operational</option>
              <option value="scheduled">scheduled</option>
              <option value="down">down</option>
            </Select>
          </Field>
          <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
          <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
          <Field label="Serial Number"><Input value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></Field>
          <Field label="Install Date"><Input type="date" value={form.install_date} onChange={(e) => setForm({ ...form, install_date: e.target.value })} /></Field>
          <Field label="Service Interval (days)"><Input type="number" value={form.service_interval_days} onChange={(e) => setForm({ ...form, service_interval_days: e.target.value })} /></Field>
          <Field label="Last Service Date"><Input type="date" value={form.last_service_date} onChange={(e) => setForm({ ...form, last_service_date: e.target.value })} /></Field>
          <Field label="Next Service Date"><Input type="date" value={form.next_service_date} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setEditOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="machine-save">Save</BtnPrimary>
          </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
